import type { WhiteLabel } from '@skytech/types'
import type { ALLOWED_WHITE_LABELS_FOR_DEALER } from '@skytech/utils'
import { isNil } from '@skytech/utils'
import dayjs from 'dayjs'

import type { OrderStatus, SubOrder } from './resources'
import { isPortingOrder, isTerminationOrder } from './types'

export const getIsOrderOpen = (status: OrderStatus) =>
  ['waitingForUser', 'waitingForDealer', 'processing', 'terminationRequest'].includes(status)

export const getCsvData = (
  orders: SubOrder[],
  translate: (id: string, values?: Record<string, string>) => string
) =>
  orders.map(order => ({
    id: order.id,
    customer: order.customer,
    orgNo: order.orgNo,
    user: isPortingOrder(order) ? order.user : null,
    phoneNumber: isPortingOrder(order) ? order.code : null,
    activationNumber: isPortingOrder(order) ? order.activationNumber : null,
    status: translate(`STATUS_SUBSCRIPTION_ORDER/${order.subStatus}`),
    activationDate:
      isPortingOrder(order) && order.activationDate
        ? dayjs(order.activationDate).format('YYYY-MM-DD')
        : null,
    isNewNumber: isPortingOrder(order) ? order.isNewNumber : null,
    isBusinessSub: isPortingOrder(order) ? order.isBusinessSub : null,
    terminationType: isTerminationOrder(order)
      ? !isNil(order.terminationTypeId)
        ? translate(`TYPE_TERMINATION/${order.terminationTypeId}`)
        : null
      : null,
    terminationDate:
      isTerminationOrder(order) && order.terminationDate
        ? dayjs(order.terminationDate).format('YYYY-MM-DD')
        : null,
    product: order.subscriptionServiceName,
    price: order.price,
    comment: order.comment,
    created: dayjs(order.orderDate).format('YYYY-MM-DD HH:mm'),
    updated: dayjs(order.orderUpdate).format('YYYY-MM-DD HH:mm'),
  }))

// Styles
const colorByWl: Partial<
  Record<
    (typeof ALLOWED_WHITE_LABELS_FOR_DEALER)[number],
    {
      base: string
      interactive: string
    }
  >
> = {
  control: {
    base: 'text-skytech-orange',
    interactive: 'hover:text-skytech-orange active:text-skytech-orange focus:text-skytech-orange',
  },
  skytech: {
    base: 'text-skytech-orange',
    interactive: 'hover:text-skytech-orange active:text-skytech-orange focus:text-skytech-orange',
  },
  tcm: {
    base: 'text-skytech-orange',
    interactive: 'hover:text-skytech-orange active:text-skytech-orange focus:text-skytech-orange',
  },
  mobit: {
    base: 'text-mobit',
    interactive: 'hover:text-mobit active:text-mobit focus:text-mobit',
  },
  bechtle: {
    base: 'text-bechtle',
    interactive: 'hover:text-bechtle active:text-bechtle focus:text-bechtle',
  },
  csi: {
    base: 'text-csi',
    interactive: 'hover:text-csi active:text-csi focus:text-csi',
  },
  elkjop: {
    base: 'text-elkjop',
    interactive: 'hover:text-elkjop active:text-elkjop focus:text-elkjop',
  },
}

export const getColorByWhiteLabel = (whiteLabel: WhiteLabel) =>
  colorByWl[whiteLabel] ?? {
    base: 'text-skytech-orange',
    interactive: 'hover:text-skytech-orange active:text-skytech-orange focus:text-skytech-orange',
  }
